import {
    Flex,
    IconButton,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
    VStack,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import React, {
    forwardRef,
    SetStateAction,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from 'react';
import { BsInfoCircleFill } from 'react-icons/bs';
import { OptionsType, OptionTypeBase } from 'react-select/src/types';
import apiClient, { ROLES } from '../../canopy-ui-components/api';
import CanopyInput from '../../canopy-ui-components/components/CanopyInput/CanopyInput';
import CanopySearch from '../../canopy-ui-components/components/CanopySelect/CanopySearch';
import CanopySelect from '../../canopy-ui-components/components/CanopySelect/CanopySelect';
import { useCurrentColors } from '../../canopy-ui-components/hooks/UseCurrentColors/UseCurrentColors';
export type MenuMappingDetailsType = {
    name: string;
    partner: any;
    role: Record<string, string | number> | null;
    user: Record<string, any> | null;
    device: Record<string, string | number> | null;
};
const debouncedgetUsers = debounce(
    apiClient.getUsersByName.bind(apiClient),
    300
);

const DEVICE_TYPE = ['desktop', 'tablet', 'mobile'];
type MenuMappingDetailsProps = {
    data: MenuMappingDetailsType;
    disabled: boolean;
    globalProps: GlobalProps;
};
export type MenuMappingDetailsSelector = {
    getDetails: () => any;
};
const MenuMappingDetails = forwardRef<
    MenuMappingDetailsSelector,
    MenuMappingDetailsProps
>(({ data, disabled, globalProps }, ref) => {
    const currentColors = useCurrentColors();
    const { t } = globalProps;
    const [partnerOptions, setPartnerOptions] = useState();
    const [name, setName] =
        useState<PropType<MenuMappingDetailsType, 'name'>>('');
    const [partner, setPartner] =
        useState<PropType<MenuMappingDetailsType, 'partner'>>();
    const [role, setRole] =
        useState<PropType<MenuMappingDetailsType, 'role'>>();
    const [user, setUser] =
        useState<PropType<MenuMappingDetailsType, 'user'>>();
    const [device, setDevice] =
        useState<PropType<MenuMappingDetailsType, 'device'>>();

    useEffect(() => {
        data['name'] && setName(data['name']);
        data?.['partner']?.id && setPartner(data['partner']);
        data['user'] && setUser(data['user']);
        data['role'] &&
            setRole({
                ...data['role'],
                label: t(data['role']?.label),
            });
        data['device'] && setDevice(data['device']);
    }, [data]);

    useEffect(() => {
        (async () => {
            const data = await apiClient.getPartners();
            !data?.error && setPartnerOptions(data);
        })();
    }, []);

    useImperativeHandle(ref, () => ({
        getDetails() {
            return {
                name,
                partner,
                user,
                role,
                device,
            };
        },
    }));

    const loadOptions = (
        inputValue: string,
        callback: (options: OptionsType<OptionTypeBase>) => void
    ) => {
        if (!inputValue || !role?.value) {
            return callback([]);
        }
        debouncedgetUsers(inputValue, role?.value, callback);
    };

    const deviceTypeOptions = useMemo(
        () =>
            DEVICE_TYPE.map((value: string) => ({
                value,
                label: t(`device.${value}`),
            })),
        []
    );

    const onNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event?.target?.value);
    };

    const onUserRoleSelection = (
        value: Record<string, string | number> | null | undefined
    ) => {
        if (role?.value !== value?.value) {
            setRole(value);
            setUser(null);
        }
    };

    const onPartnerSelection = (
        value: SetStateAction<
            Record<string, string | number> | null | undefined
        >
    ) => {
        setPartner(value);
    };

    const onUserSelection = async (
        value: SetStateAction<Record<string, any> | null | undefined>
    ) => {
        setUser(value);
    };

    const onDeviceTypeSelection = async (
        value: SetStateAction<
            Record<string, string | number> | null | undefined
        >
    ) => {
        setDevice(value);
    };

    return (
        <Flex width="100%">
            <VStack
                width="100%"
                align="flex-start"
                my={4}
                spacing="26px">
                <CanopyInput
                    onChange={onNameChange}
                    value={name}
                    placeholder={t('ui.mm.menu-mapping.name')}
                    opacity={disabled ? '0.6 !important' : '1'}
                    fontSize="13px"
                    height="30px"
                    boxShadow="none"
                    backgroundColor="transparent"
                    color={currentColors.brand['10']}
                    _hover={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}
                    _focus={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}
                    _active={{
                        color: currentColors.brand['10'],
                        backgroundColor: 'transparent',
                    }}
                    isDisabled={disabled}></CanopyInput>
                <CanopySelect
                    id="partner"
                    placeholder={t('ui.mm.menu-mapping.select-partner')}
                    isClearable={true}
                    value={partner}
                    options={partnerOptions}
                    getOptionLabel={(option: any) => `${option.partnerName}`}
                    getOptionValue={(option: any) => option.id}
                    onChange={onPartnerSelection}
                    color={currentColors.brand['10']}
                    height="30px"
                    mt="10px"
                    isDisabled={disabled}></CanopySelect>
                <CanopySelect
                    id="roles"
                    placeholder={t('ui.mm.menu-mapping.select-role')}
                    isClearable={true}
                    value={role}
                    options={ROLES.map(({ label, value }) => ({
                        value,
                        label: t(label),
                    }))}
                    onChange={onUserRoleSelection}
                    color={currentColors.brand['10']}
                    height="30px"
                    mt="10px"
                    isDisabled={disabled}></CanopySelect>

                <CanopySearch
                    // isMulti
                    isDisabled={!role || disabled}
                    isClearable={true}
                    loadOptions={loadOptions}
                    onChange={onUserSelection}
                    placeholder={t('ui.mm.menu-mapping.select-user')}
                    t={t}
                    isMultiLineOption
                    getOptionLabel={(option: any) =>
                        `${option.displayName} \n ${option.username}`
                    }
                    getOptionValue={(option: any) => option.id}
                    value={user}
                    isSearchable={Boolean(!user)}
                    loadingMessage={() => `${t('loading')}...`}
                    height="30px"
                />
                <Flex
                    width="100%"
                    align="center">
                    <CanopySelect
                        id="deviceType"
                        height="30px"
                        isClearable={true}
                        placeholder={t('ui.mm.menu-mapping.select-device-type')}
                        value={device}
                        options={deviceTypeOptions}
                        getOptionLabel={(option: any) => option.label}
                        getOptionValue={(option: any) => option.value}
                        onChange={onDeviceTypeSelection}
                        color={currentColors.brand['10']}
                        isDisabled={disabled}></CanopySelect>
                    <Popover
                        trigger="hover"
                        placement="right">
                        {
                            <PopoverTrigger>
                                <IconButton
                                    background="transparent"
                                    cursor="pointer"
                                    border="none"
                                    aria-label="strategy-date-info"
                                    width="12px !important"
                                    height="12px !important"
                                    backgroundColor="transparent"
                                    color={currentColors.brand['300']}
                                    variant="ghost"
                                    _hover={{
                                        backgroundColor: 'transparent',
                                    }}
                                    _focus={{
                                        border: '0px',
                                        backgroundColor: 'transparent',
                                        outline: 'none',
                                    }}
                                    _active={{
                                        border: '0px',
                                    }}
                                    icon={<BsInfoCircleFill />}></IconButton>
                            </PopoverTrigger>
                        }
                        <Portal>
                            <PopoverContent>
                                <PopoverArrow />
                                <PopoverBody className="popover-text">
                                    {t('ui.mm.menu-mapping.device-note')}
                                </PopoverBody>
                            </PopoverContent>
                        </Portal>
                    </Popover>
                </Flex>
            </VStack>
        </Flex>
    );
});
export default React.memo(MenuMappingDetails);
